import { z } from 'zod'

// TODO: rename KnownGameConfig and UnknownGameConfig
// Currently "Game" is a bit confusing because it is used to talk about the Widget but also the RaceConfiguration
export type BettingMode = 'classic' | 'chip-placement'

export interface MarketOptionConfig {
  id: string
  name: string
  color: string
  image?: string
  multiplier: number
  bettable: boolean
}

export interface MarketConfig {
  id: string
  type: 'main' | 'side_bet'
  label: string
  description?: string
  probabilities: Record<string, number>
  limits: { PickWinner: { min: number; max: number } }
  maxMultiplier: number
  options: MarketOptionConfig[]
  displayOrder: number
}

export interface KnownGameConfig {
  lobbyDisabled: boolean
  externalLobbyUrl?: string
  externalDepositUrl?: string
  platform: 'MOBILE' | 'DESKTOP'
  language?: string
  country?: string
  /**
   * Units of `bet.currency` per 1 USD, stamped by the Go config generator.
   * Social feed amounts are USD on the wire; this converts them for display.
   * Absent = render USD unchanged (crypto, or a rate lookup failure).
   */
  usdRate?: number
  bettingMode?: BettingMode
  markets?: MarketConfig[]
  bet: {
    probabilities: Record<string, number>
    equalOdds: boolean
    currency: string
    maxBetAmount: number
    minBetAmount: number
    defaultBetMultiplier: number
    chipMultipliers: number[]
    maxMultiplier: number
    maxPayout: number
    limits: {
      PickWinner: {
        min: number
        max: number
      }
      ReverseForecast?: {
        min: number
        max: number
      }
      StraightForecast?: {
        min: number
        max: number
      }
      CombinationTricast?: {
        min: number
        max: number
      }
      Tricast?: {
        min: number
        max: number
      }
    }
  }
}

export type UnknownGameConfig = Partial<KnownGameConfig>

const BetLimitsSchema = z.object({
  min: z.number(),
  max: z.number()
})

const MarketOptionSchema = z.object({
  id: z.string(),
  name: z.string().optional().default(''),
  color: z.string().optional().default(''),
  image: z.string().optional(),
  multiplier: z.number(),
  bettable: z.boolean()
})

const MarketConfigSchema = z.object({
  id: z.string(),
  type: z.enum(['main', 'side_bet']),
  label: z.string(),
  description: z.string().optional(),
  probabilities: z.record(z.number()),
  limits: z.object({ PickWinner: BetLimitsSchema }),
  maxMultiplier: z.number(),
  options: z.array(MarketOptionSchema),
  displayOrder: z.number()
})

const KnownGameConfigSchema = z.object({
  lobbyDisabled: z.boolean(),
  externalLobbyUrl: z.string().optional(),
  externalDepositUrl: z.string().optional(),
  platform: z.enum(['MOBILE', 'DESKTOP']),
  language: z.string().optional(),
  country: z.string().optional(),
  usdRate: z.number().optional(),
  bettingMode: z.enum(['classic', 'chip-placement']).optional(),
  markets: z.array(MarketConfigSchema).optional(),
  bet: z.object({
    currency: z.string(),
    probabilities: z.record(z.number()).optional().nullable(),
    maxBetAmount: z.number(),
    minBetAmount: z.number(),
    defaultBetMultiplier: z.number(),
    chipMultipliers: z.array(z.number()),
    maxMultiplier: z.number(),
    maxPayout: z.number(),
    limits: z
      .object({
        PickWinner: BetLimitsSchema,
        ReverseForecast: BetLimitsSchema.optional(),
        StraightForecast: BetLimitsSchema.optional(),
        CombinationTricast: BetLimitsSchema.optional(),
        Tricast: BetLimitsSchema.optional()
      })
      .optional()
      .nullable()
  })
})

export type GameConfig = UnknownGameConfig | KnownGameConfig

export const unknownGameConfig: UnknownGameConfig = {}

export const isKnownGameConfig = (gameConfig: GameConfig): gameConfig is KnownGameConfig =>
  KnownGameConfigSchema.safeParse(gameConfig).success
